import { useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import type { WatchComment } from "@/hooks/use-watch";

type Props = {
  /** Only rendered when true — driven by Settings → View on Asset → Anchored Comments. */
  visible: boolean;
  /** Stack-wide comments. Filtered here to anchor-carrying rows. */
  comments: WatchComment[];
  /** Click a pin → seek to that comment's timecode (if it has one). */
  onSeek?: (seconds: number) => void;
};

/**
 * Renders a persistent pin at every anchor comment's positionX/Y.
 * Matches the anchor-drop cursor style — white body, black border. Hover
 * reveals author + timecode + content preview. Click seeks the video.
 */
export function AnchorPinsDisplay({ visible, comments, onSeek }: Props) {
  const pins = useMemo(() => {
    const seq = [...comments].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    return seq
      .map((c, i) => ({ comment: c, sequence: i + 1 }))
      .filter(
        ({ comment: c }) =>
          typeof c.positionX === "number" && typeof c.positionY === "number",
      );
  }, [comments]);

  if (!visible || pins.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-10">
      {pins.map(({ comment, sequence }) => (
        <AnchorPin
          key={comment.id}
          x={comment.positionX!}
          y={comment.positionY!}
          author={comment.author?.name || (comment.author?.type === "guest" ? "Guest" : "Someone")}
          sequence={sequence}
          content={comment.content}
          timestampSeconds={comment.timestampSeconds}
          onSeek={onSeek}
        />
      ))}
    </div>
  );
}

function AnchorPin({
  x,
  y,
  author,
  sequence,
  content,
  timestampSeconds,
  onSeek,
}: {
  x: number;
  y: number;
  author: string;
  sequence: number;
  content: string;
  timestampSeconds: number | null;
  onSeek?: (seconds: number) => void;
}) {
  const [hovered, setHovered] = useState(false);

  const canSeek = onSeek && typeof timestampSeconds === "number";

  return (
    <div
      className="pointer-events-auto absolute"
      style={{
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        transform: "translate(-50%, -100%)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={() => canSeek && onSeek!(timestampSeconds!)}
        disabled={!canSeek}
        aria-label={`Anchor comment #${sequence} by ${author}`}
        className="grid place-items-center transition-transform hover:scale-110 disabled:cursor-default"
      >
        <MapPin
          className="h-5 w-5 fill-white text-black drop-shadow-md"
          strokeWidth={2}
        />
      </button>
      {hovered && (
        <div
          className="pointer-events-none absolute left-1/2 z-20 min-w-48 max-w-64 -translate-x-1/2 whitespace-normal rounded-md border border-white/10 bg-neutral-900 px-2 py-1.5 text-xs text-white shadow-lg"
          style={{ bottom: 22 }}
        >
          <div className="flex items-center justify-between gap-2 text-[10px] text-white/60">
            <span>#{sequence} · {author}</span>
            {timestampSeconds != null && <span>{formatTimecode(timestampSeconds)}</span>}
          </div>
          <div className="mt-0.5 line-clamp-3 text-sm text-white">{content}</div>
        </div>
      )}
    </div>
  );
}

function formatTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const totalMs = Math.floor(seconds * 1000);
  const hh = Math.floor(totalMs / 3_600_000);
  const mm = Math.floor((totalMs % 3_600_000) / 60_000);
  const ss = Math.floor((totalMs % 60_000) / 1000);
  const ms = totalMs % 1000;
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}.${padMs(ms)}`;
}
const pad = (n: number) => n.toString().padStart(2, "0");
const padMs = (n: number) => n.toString().padStart(3, "0");
